import { createSlice, createAsyncThunk, createEntityAdapter, EntityState } from '@reduxjs/toolkit'

import { DetailedTradingAccount, getTradingAccount } from '@api/orval/apiHooks'
import { RootState } from '@store/store'

const accountAdapter = createEntityAdapter<DetailedTradingAccount, number>({
  selectId: (account) => account.id,
  sortComparer: (a, b) => b.id - a.id,
})

interface AccountDataState extends EntityState<DetailedTradingAccount, number> {
  accountId?: number
  loading: boolean
  error: string | null
}

const initialState: AccountDataState = accountAdapter.getInitialState({
  accountId: undefined as number | undefined,
  loading: false,
  error: null as string | null,
})

// TODO(nikita): ?remove? account data slice
export const fetchAccountDataThunk = createAsyncThunk<
  DetailedTradingAccount,
  number,
  { state: RootState; rejectValue: string }
>('accounts/fetchAccountData', async (taId, { getState, rejectWithValue }) => {
  const state = getState()
  const existing = state.accounts.entities[taId]
  if (existing) return existing

  try {
    const response = await getTradingAccount(taId)
    if (!response) throw new Error('Account not found')

    return response
  } catch (error) {
    console.error('Error fetching account data:', error)

    return rejectWithValue('Failed to fetch account data')
  }
})

const accountSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    addAccountData: accountAdapter.upsertOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountDataThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAccountDataThunk.fulfilled, (state, { payload }) => {
        accountAdapter.upsertOne(state, payload)
        state.loading = false
        state.accountId = payload.id
      })
      .addCase(fetchAccountDataThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload ?? 'Unknown error'
      })
  },
})

export const { addAccountData } = accountSlice.actions

// Adapter selectors
const accountDataSelectors = accountAdapter.getSelectors<RootState>((state) => state.accounts)

export const selectAccountById = (state: RootState, accountId?: number) => {
  if (typeof accountId === 'number') {
    return accountDataSelectors.selectById(state, accountId)
  } else if (typeof state.accounts.accountId === 'number') {
    return accountDataSelectors.selectById(state, state.accounts.accountId)
  }

  return null
}

export const accountReducer = accountSlice.reducer
